/**
 * Clientes — Página de gestión de clientes dentro del módulo Ingeniería.
 * Los datos cargados acá se usan en Presupuestos y alimentan el dropdown de Compras.
 * CRUD contra /api/listas/clientes (el código CL-X### lo genera el backend).
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Users, Plus, Search, Pencil, Trash2, Loader2, Copy, Check, History, UserPlus, IdCard,
} from "lucide-react";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import ClienteFormModal from "../../components/ClienteFormModal";
import ClienteTrazabilidadModal from "../../components/ClienteTrazabilidadModal";
import QuickPotencialModal from "../../components/QuickPotencialModal";
import FichaClienteModal from "../../components/FichaClienteModal";

// Auditoría 2026-08-27, CAMBIO 7 — estados alineados al pedido del cliente
// (potencial|activo|perdido), reemplazan prospecto/inactivo.
const ESTADO_BADGE = {
  potencial: "bg-amber-100 text-amber-700",
  activo: "bg-emerald-100 text-emerald-700",
  perdido: "bg-gray-100 text-gray-500",
};
const ESTADO_TABS = [
  { key: "todos", label: "Todos" },
  { key: "potencial", label: "Potenciales" },
  { key: "activo", label: "Activos" },
  { key: "perdido", label: "Perdidos" },
];

function CodeBadge({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) { /* noop */ }
  };
  return (
    <button onClick={copy}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md font-mono text-[11.5px] font-bold tracking-wide transition-all hover:brightness-110"
      style={{ background: "#2E6DA415", color: "#2E6DA4", border: "1px solid #2E6DA430" }}
      title="Click para copiar">
      {code}
      {copied ? <Check size={10} /> : <Copy size={10} className="opacity-50" />}
    </button>
  );
}

export default function Clientes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [estadoTab, setEstadoTab] = useState("todos");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [trazaCliente, setTrazaCliente] = useState(null);
  const [quickPotencial, setQuickPotencial] = useState(false);
  const [fichaCliente, setFichaCliente] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/listas/clientes`);
      setItems(r.data || []);
    } catch (_e) {
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = async () => {
    setOpen(false);
    setEditing(null);
    await load();
  };

  const handleDelete = async (x) => {
    if (!window.confirm("¿Seguro que querés eliminar este cliente? Si tiene presupuestos asociados va a quedar como cliente histórico.")) return;
    try {
      await axios.delete(`${API}/listas/clientes/${x._id}`);
      toast.success("Cliente eliminado");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar el cliente");
    }
  };

  const handleEstadoChange = async (x, estado) => {
    try {
      await axios.put(`${API}/listas/clientes/${x._id}/estado`, { estado });
      toast.success("Estado actualizado");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo cambiar el estado"); }
  };

  const filtered = items.filter((x) => {
    if (estadoTab !== "todos" && (x.estado || "activo") !== estadoTab) return false;
    const q = search.toLowerCase();
    return !q || [x.cliente, x.codigo, x.ruc_ci, x.celular, x.email, x.direccion]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="clientes-page">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <Users size={22} className="text-[#2E6DA4]" /> Clientes
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestión de clientes — los datos cargados acá se usan en Presupuestos y alimentan el dropdown de Compras.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, código, RUC..."
              className="pl-9 pr-3 py-2 text-[13px] rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-gray-400 focus:outline-none w-64"
              data-testid="clientes-search"
            />
          </div>
          <button
            onClick={() => setQuickPotencial(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-semibold text-white shadow-sm hover:brightness-110 transition-all bg-amber-500"
            data-testid="cliente-quick-potencial-btn"
          >
            <UserPlus size={14} /> <span>Alta Rápida Potencial</span>
          </button>
          <button
            onClick={() => { setEditing(null); setOpen(true); }}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-semibold text-white shadow-sm hover:brightness-110 transition-all bg-[#2E6DA4]"
            data-testid="cliente-new-btn"
          >
            <Plus size={14} /> <span>Nuevo Cliente</span>
          </button>
        </div>
      </div>

      {/* Filtro por estado — dos listas (Clientes / Potenciales) + Perdidos */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="cliente-estado-tabs">
        {ESTADO_TABS.map((t) => (
          <button key={t.key} onClick={() => setEstadoTab(t.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${estadoTab === t.key ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
            data-testid={`cliente-estado-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Table / states */}
      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-400">
          <Loader2 size={20} className="mx-auto animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3 bg-[#2E6DA415] text-[#2E6DA4]">
            <Users size={22} />
          </div>
          <h4 className="text-[14px] font-semibold text-gray-800">
            {search ? "Sin resultados para tu búsqueda" : "Aún no hay clientes"}
          </h4>
          {!search && (
            <button onClick={() => { setEditing(null); setOpen(true); }}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-[#2E6DA4]">
              <Plus size={14} /> Crear Cliente
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-gray-50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 font-semibold">Código</th>
                  <th className="px-4 py-3 font-semibold">Cliente</th>
                  <th className="px-4 py-3 font-semibold">RUC/CI</th>
                  <th className="px-4 py-3 font-semibold">Celular</th>
                  <th className="px-4 py-3 font-semibold">Dirección</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Estado</th>
                  <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((x) => (
                  <tr key={x._id} className="border-t border-gray-100 hover:bg-gray-50/60" data-testid={`cliente-row-${x.codigo}`}>
                    <td className="px-4 py-2.5"><CodeBadge code={x.codigo} /></td>
                    <td className="px-4 py-2.5 font-semibold text-gray-900">{x.cliente}</td>
                    <td className="px-4 py-2.5 text-gray-600 font-mono text-[12px]">{x.ruc_ci || "—"}</td>
                    <td className="px-4 py-2.5 text-gray-600">{x.celular || "—"}</td>
                    <td className="px-4 py-2.5 text-gray-600 truncate max-w-[200px]">{x.direccion || "—"}</td>
                    <td className="px-4 py-2.5 text-gray-600">{x.email || "—"}</td>
                    <td className="px-4 py-2.5">
                      <select value={x.estado || "activo"} onChange={(e) => handleEstadoChange(x, e.target.value)}
                        className={`text-xs font-semibold rounded-full px-2 py-1 border-0 ${ESTADO_BADGE[x.estado] || ESTADO_BADGE.activo}`}
                        data-testid={`cliente-estado-${x.codigo}`}>
                        <option value="potencial">Potencial</option>
                        <option value="activo">Activo</option>
                        <option value="perdido">Perdido</option>
                      </select>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => setFichaCliente(x)}
                          className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600" title="Ficha del cliente"
                          data-testid={`cliente-ficha-${x.codigo}`}>
                          <IdCard size={13} />
                        </button>
                        <button onClick={() => setTrazaCliente(x)}
                          className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-600" title="Trazabilidad"
                          data-testid={`cliente-traza-${x.codigo}`}>
                          <History size={13} />
                        </button>
                        <button onClick={() => { setEditing(x); setOpen(true); }}
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600" title="Editar"
                          data-testid={`cliente-edit-${x.codigo}`}>
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => handleDelete(x)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" title="Eliminar"
                          data-testid={`cliente-delete-${x.codigo}`}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {open && (
        <ClienteFormModal
          initial={editing}
          onClose={() => { setOpen(false); setEditing(null); }}
          onSaved={handleSaved}
        />
      )}
      {trazaCliente && (
        <ClienteTrazabilidadModal cliente={trazaCliente} onClose={() => setTrazaCliente(null)} />
      )}
      {quickPotencial && (
        <QuickPotencialModal onClose={() => setQuickPotencial(false)} onSaved={async () => { setQuickPotencial(false); await load(); }} />
      )}
      {fichaCliente && (
        <FichaClienteModal cliente={fichaCliente} onClose={() => setFichaCliente(null)} onSaved={load} />
      )}
    </div>
  );
}
